import React, { Fragment, useContext, useEffect, useMemo, useRef, useState } from "react";
import { GlobalContext } from "../../../Context/GlobalContext";
import { Toaster, toast } from "react-hot-toast";
import { Dialog, Transition } from "@headlessui/react";
import {
  MessagesSquare, Search, RefreshCcw, MoreVertical, Tag, Plus, ChevronDown, Paperclip,
  Copy, ShieldBan, Sparkles, ChevronLeft
} from "lucide-react";
import ContactsTab from "./subComponents/ContactsTab";

import useSupport from "../../Services/SupportServices/useSupport";

/** Utility */
const cls = (...s) => s.filter(Boolean).join(" ");

export default function Support() {
  const {getChatUserList, getSupportChatsAdmin, sendSupportChatAdmin}=useSupport();

  const { api, adminId } = useContext(GlobalContext);

  // layout
  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" ? window.innerWidth < 1108 : false);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 1108);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // list + selection
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [search, setSearch] = useState("");
  const [activeUser, setActiveUser] = useState(null);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter(
      (u) =>
        !q ||
        String(u.user_name || "").toLowerCase().includes(q) ||
        String(u.id || "").toLowerCase().includes(q)
    );
  }, [users, search]);

  // chat
  const [chat, setChat] = useState([]);
  const [chatBusy, setChatBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const listRef = useRef(null);

  // status + tags quick actions
  const [statusOpen, setStatusOpen] = useState(false);
  const [status, setStatus] = useState("Open");
  const statusOptions = ["Open", "Closed"];

  const [tagPopoverOpen, setTagPopoverOpen] = useState(false);
  const [tagModalOpen, setTagModalOpen] = useState(false);
  const [tags, setTags] = useState(["example"]);
  const [newTag, setNewTag] = useState("");

  const [moreOpen, setMoreOpen] = useState(false);

  // modals
  const [createTicketOpen, setCreateTicketOpen] = useState(false);
  const [sessionOpen, setSessionOpen] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);
  const [banOpen, setBanOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [banIp, setBanIp] = useState(false);

  // tabs (right side)
  const [tab, setTab] = useState("chat"); // chat | contacts

  /** Load users */
 const loadUsers = async () => {
  setLoadingUsers(true);

  try {
    const result =
      await getChatUserList(adminId);

    if (result.success) {
      const list = result.data;

      setUsers(list);

      if (!activeUser && list.length) {
        setActiveUser(list[0]);

        // fetch their chat
        loadChat(list[0]?.user_mobile);
      }
    } else {
      toast.error(result.message);
    }
  } catch (e) {
    toast.error("Error loading users");
  } finally {
    setLoadingUsers(false);
  }
};

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminId]);

  /** Load chat for a given mobile */
  const loadChat = async (mobile) => {
  if (!mobile) return;

  setChatBusy(true);

  try {
    const result =
      await getSupportChatsAdmin({
        mobile,
        message: "",
      });

    if (result.success) {
      setChat(result.data);

      // scroll to end
      setTimeout(() => {
        listRef.current?.scrollTo({
          top: 9e6,
          behavior: "smooth",
        });
      }, 60);
    } else {
      toast.error(result.message);
      setChat([]);
    }
  } catch (e) {
    toast.error("Error loading chat");
    setChat([]);
  } finally {
    setChatBusy(false);
  }
  };

  /** Send message */
 const sendMessage = async () => {
  if (!msg.trim()) return;
  if (!activeUser?.user_mobile) return;

  const payload = {
    mobile: activeUser.user_mobile,
    message: msg.trim(),
  };

  try {
    const result =
      await sendSupportChatAdmin(payload);

    if (result.success) {
      setMsg("");

      // Optimistic append
      setChat((prev) => [
        ...prev,
        {
          type: "SENT",
          message: payload.message,
          datetime: new Date().toLocaleString(),
        },
      ]);

      // Refresh from server
      loadChat(activeUser.user_mobile);

    } else {
      toast.error(result.message);
    }
  } catch (e) {
    toast.error("Error sending message");
  }
};


  const chooseUser = (u) => {
    setActiveUser(u);
    setTab(isMobile ? "chat" : tab);
    loadChat(u?.user_mobile);
  };

  /** Copy transcript plain text */
  const transcriptText = useMemo(() => {
    const lines = [];
    if (chat?.length) {
      lines.push(`Conversation started on ${chat[0]?.datetime ?? ""}`);
      lines.push("=======================================================");
    }
    chat?.forEach((m) => {
      const time = String(m?.datetime || "").split(",")?.[1]?.trim()?.split(" ")?.[0] || "";
      const who = m?.type === "RECEIVED" ? (activeUser?.id || "User") : "Customer Support";
      lines.push(`[${time}] ${who}: ${m?.message || ""}`);
    });
    return lines.join("\n");
  }, [chat, activeUser]);

  const copyTranscript = async () => {
    try {
      await navigator.clipboard.writeText(transcriptText);
      toast.success("Copied conversation");
    } catch {
      toast.error("Copy failed");
    }
  };

  return (
    <div className="p-3 lg:p-5">
      <Toaster position="top-right" />

      {/* Header */}
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="grid h-10 w-10 place-items-center rounded-xl bg-[#01C37D]/10 text-[#01C37D]">
            <MessagesSquare size={18} />
          </div>
          <div>
            <h1 className="text-xl font-semibold">Support</h1>
            <p className="text-sm text-gray-500">Live chat, contacts & quick actions</p>
          </div>
        </div>
        <button
          className="inline-flex h-9 items-center gap-2 rounded-xl border px-3 text-sm hover:bg-gray-50"
          onClick={loadUsers}
          disabled={loadingUsers}
        >
          <RefreshCcw size={16} /> {loadingUsers ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {/* Main */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-12">
        {/* Left: list */}
        {(!isMobile || tab !== "chat") && (
          <div className="lg:col-span-4 xl:col-span-3 rounded-2xl border bg-white">
            <div className="flex items-center gap-2 border-b p-3">
              <div className="flex h-10 flex-1 items-center gap-2 rounded-xl border px-3">
                <Search size={16} className="text-gray-500" />
                <input
                  className="h-full w-full text-sm outline-none"
                  placeholder="Search by name or ID…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>

            <div className="max-h-[78vh] overflow-auto">
              {filtered.map((u) => {
                const initials =
                  u?.profilePic ||
                  String(u?.user_name || "")
                    .split(" ")
                    .map((w) => w[0])
                    .slice(0, 2)
                    .join("")
                    .toUpperCase();
                const selected = activeUser?.id === u.id;
                return (
                  <button
                    key={u.id}
                    onClick={() => chooseUser(u)}
                    className={cls(
                      "flex w-full items-center gap-3 border-b px-3 py-2 text-left hover:bg-gray-50",
                      selected && "bg-emerald-50/50"
                    )}
                  >
                    <div className="grid h-12 w-12 place-items-center rounded-lg bg-orange-500 font-semibold text-white">
                      {initials || "U"}
                    </div>
                    <div className="min-w-0">
                      <div className="truncate text-sm font-semibold">{u.user_name}</div>
                      <div className="truncate text-xs text-gray-500">{u.id}</div>
                    </div>
                  </button>
                );
              })}
              {!filtered.length && (
                <div className="p-6 text-center text-sm text-gray-500">{loadingUsers ? "Loading…" : "No users"}</div>
              )}
            </div>
          </div>
        )}

        {/* Right: chat / contacts */}
        {(!isMobile || tab === "chat") && (
          <div className="lg:col-span-8 xl:col-span-9 rounded-2xl border bg-white">
            {!activeUser?.user_mobile ? (
              <div className="grid h-[78vh] place-items-center p-6 text-center text-gray-500">
                <div className="space-y-2">
                  <div className="mx-auto grid h-24 w-24 place-items-center rounded-2xl border">
                    <MessagesSquare />
                  </div>
                  <div className="text-sm">Pick a user from the list to start</div>
                </div>
              </div>
            ) : (
              <div className="flex h-[78vh] flex-col">
                {/* Top bar */}
                <div className="flex items-center justify-between border-b p-3">
                  <div className="flex items-center gap-2">
                    {isMobile && (
                      <button
                        className="mr-1 inline-flex h-9 w-9 items-center justify-center rounded-lg border"
                        onClick={() => setTab("contacts" === tab ? "chat" : "contacts")}
                      >
                        <ChevronLeft size={16} />
                      </button>
                    )}
                    <div className="grid h-8 w-8 place-items-center rounded-md bg-orange-500 text-xs font-semibold text-white">
                      {(activeUser?.profilePic ||
                        String(activeUser?.user_name || "")
                          .split(" ")
                          .map((w) => w[0])
                          .slice(0, 2)
                          .join("")
                          .toUpperCase()) || "U"}
                    </div>
                    <div className="min-w-0">
                      <div className="truncate text-sm font-semibold">{activeUser?.user_name}</div>
                      <div className="truncate text-xs text-gray-500">{activeUser?.id}</div>
                    </div>
                  </div>

                  {/* Quick actions */}
                  <div className="flex items-center gap-2">
                    {/* Status */}
                    <div className="relative">
                      <button
                        className="inline-flex items-center rounded-xl border px-2.5 py-1.5 text-xs"
                        onClick={() => {
                          setStatusOpen((s) => !s);
                          setTagPopoverOpen(false);
                          setMoreOpen(false);
                        }}
                      >
                        {status} <ChevronDown size={14} className="ml-1 opacity-70" />
                      </button>
                      {statusOpen && (
                        <div className="absolute right-0 z-10 mt-2 w-40 overflow-hidden rounded-xl border bg-white shadow-lg">
                          {statusOptions.map((s) => (
                            <button
                              key={s}
                              className={cls(
                                "flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50",
                                s === "Open" && "text-amber-700"
                              )}
                              onClick={() => {
                                setStatus(s);
                                setStatusOpen(false);
                              }}
                            >
                              <span
                                className={cls(
                                  "h-2 w-2 rounded-full",
                                  s === "Open" ? "bg-amber-500" : "bg-gray-400"
                                )}
                              />
                              {s}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Tags */}
                    <div className="relative">
                      <button
                        className="inline-flex h-8 w-8 items-center justify-center rounded-xl border"
                        onClick={() => {
                          setTagPopoverOpen((p) => !p);
                          setStatusOpen(false);
                          setMoreOpen(false);
                        }}
                        title="Tags"
                      >
                        <Tag size={16} />
                      </button>

                      {tagPopoverOpen && (
                        <div className="absolute right-0 z-10 mt-2 w-56 rounded-xl border bg-white p-2 shadow-lg">
                          <div className="flex items-center gap-2">
                            <input
                              className="h-9 flex-1 rounded-lg border px-2 text-sm outline-none"
                              placeholder="Search or create…"
                              value={newTag}
                              onChange={(e) => setNewTag(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" && newTag.trim()) {
                                  if (!tags.includes(newTag.trim())) setTags((t) => [...t, newTag.trim()]);
                                  setNewTag("");
                                }
                              }}
                            />
                            <button
                              className="inline-flex h-9 items-center justify-center rounded-lg bg-[#01C37D] px-3 text-xs font-medium text-white"
                              onClick={() => setTagModalOpen(true)}
                            >
                              <Plus size={14} />
                            </button>
                          </div>
                          <div className="mt-2 flex flex-wrap gap-2">
                            {tags.map((t) => (
                              <span
                                key={t}
                                className="inline-flex items-center rounded-full border bg-gray-50 px-2 py-0.5 text-xs"
                              >
                                {t}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* More */}
                    <div className="relative">
                      <button
                        className="inline-flex h-8 w-8 items-center justify-center rounded-xl border"
                        onClick={() => {
                          setMoreOpen((m) => !m);
                          setStatusOpen(false);
                          setTagPopoverOpen(false);
                        }}
                        title="More"
                      >
                        <MoreVertical size={16} />
                      </button>
                      {moreOpen && (
                        <div className="absolute right-0 z-10 mt-2 w-56 overflow-hidden rounded-xl border bg-white shadow-lg">
                          <button
                            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50"
                            onClick={() => {
                              setCreateTicketOpen(true);
                              setMoreOpen(false);
                            }}
                          >
                            <Plus size={16} /> Create Ticket
                          </button>
                          <button
                            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50"
                            onClick={() => {
                              setSessionOpen(true);
                              setMoreOpen(false);
                            }}
                          >
                            <Paperclip size={16} /> Session Information
                          </button>
                          <button
                            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50"
                            onClick={() => {
                              setCopyOpen(true);
                              setMoreOpen(false);
                            }}
                          >
                            <Copy size={16} /> Copy
                          </button>
                          <button
                            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50"
                            onClick={() => {
                              setBanOpen(true);
                              setMoreOpen(false);
                            }}
                          >
                            <ShieldBan size={16} /> Ban
                          </button>
                          <button
                            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50"
                            onClick={() => {
                              setFeedbackOpen(true);
                              setMoreOpen(false);
                            }}
                          >
                            <Sparkles size={16} /> AI Feedback
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Tabs */}
                <div className="flex items-center">
                  <button
                    className={cls(
                      "flex-1 border-b-4 px-3 py-2 text-center text-sm font-semibold",
                      tab === "chat" ? "border-[#01C37D] text-[#01C37D]" : "border-transparent text-gray-500"
                    )}
                    onClick={() => setTab("chat")}
                  >
                    Chat
                  </button>
                  <button
                    className={cls(
                      "flex-1 border-b-4 px-3 py-2 text-center text-sm font-semibold",
                      tab === "contacts" ? "border-[#01C37D] text-[#01C37D]" : "border-transparent text-gray-500"
                    )}
                    onClick={() => setTab("contacts")}
                  >
                    Contacts
                  </button>
                </div>

                {/* Body */}
                {tab === "chat" ? (
                  <div className="flex min-h-0 flex-1 flex-col">
                    {/* Date chip */}
                    {!!chat?.length && (
                      <div className="flex justify-center p-3">
                        <span className="rounded-md bg-gray-100 px-3 py-1 text-xs">
                          {chat[0]?.datetime || ""}
                        </span>
                      </div>
                    )}

                    {/* Messages */}
                    <div ref={listRef} className="min-h-0 flex-1 space-y-3 overflow-y-auto p-3">
                      {chatBusy ? (
                        <div className="py-10 text-center text-sm text-gray-500">Loading…</div>
                      ) : !chat?.length ? (
                        <div className="py-10 text-center text-sm text-gray-500">No messages yet.</div>
                      ) : (
                        chat.map((m, idx) => {
                          const me = m?.type !== "RECEIVED";
                          const bubble = (
                            <div
                              className={cls(
                                "max-w-[80%] rounded-2xl border px-3 py-2 text-sm",
                                me
                                  ? "ml-auto border-emerald-200 bg-emerald-50 text-emerald-800"
                                  : "mr-auto border-gray-200 bg-gray-50 text-gray-800"
                              )}
                            >
                              <div className="whitespace-pre-wrap">{m?.message || ""}</div>
                              <div className="mt-1 text-right text-[11px] text-gray-500">{m?.datetime || ""}</div>
                            </div>
                          );
                          return <div key={idx}>{bubble}</div>;
                        })
                      )}
                    </div>

                    {/* Composer */}
                    <div className="flex items-center gap-2 border-t p-3">
                      <input
                        className="h-10 flex-1 rounded-xl border px-3 text-sm outline-none"
                        placeholder="Type a message…"
                        value={msg}
                        onChange={(e) => setMsg(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && sendMessage()}
                      />
                      <button
                        onClick={sendMessage}
                        className="inline-flex h-10 items-center justify-center rounded-xl bg-[#01C37D] px-4 text-sm font-medium text-white hover:bg-[#01C37D]/90"
                      >
                        Send
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="min-h-0 flex-1 overflow-auto p-3">
                    <ContactsTab expandedUserDetails={activeUser} chat={chat} />
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Create Ticket */}
      <Modal show={createTicketOpen} onClose={() => setCreateTicketOpen(false)} title="Create Ticket">
        <div className="space-y-3">
          <div>
            <div className="mb-1 text-xs text-gray-500">Contact</div>
            <div className="flex items-center justify-between rounded-xl border p-2 text-sm">
              <div className="flex items-center gap-2">
                <span className="grid h-6 w-6 place-items-center rounded-md bg-orange-500 text-white">
                  {(activeUser?.user_name || "U").slice(0, 1).toUpperCase()}
                </span>
                <span className="font-medium">{activeUser?.id}</span>
              </div>
            </div>
          </div>

          <div>
            <div className="mb-1 text-xs text-gray-500">Email Address</div>
            <input className="h-10 w-full rounded-xl border px-3 text-sm outline-none" placeholder="example@gmail.com" />
          </div>

          <div>
            <div className="mb-1 text-xs text-gray-500">Subject</div>
            <input
              className="h-10 w-full rounded-xl border px-3 text-sm outline-none"
              value={`RE: Chat with ${activeUser?.id || ""}`}
              onChange={() => {}}
            />
          </div>

          <div>
            <div className="mb-1 text-xs text-gray-500">Message</div>
            <textarea rows={6} className="w-full rounded-xl border px-3 py-2 text-sm outline-none" />
          </div>

          <div>
            <div className="mb-1 text-xs text-gray-500">Attachments</div>
            <div className="rounded-xl border border-dashed p-6 text-center text-sm text-gray-500">
              Drop your files here or <span className="text-[#01C37D]">browse</span>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <button className="h-9 rounded-xl border px-3 text-sm" onClick={() => setCreateTicketOpen(false)}>
              Cancel
            </button>
            <button
              className="h-9 rounded-xl bg-[#01C37D] px-4 text-sm font-medium text-white"
              onClick={() => {
                toast.success("Ticket created");
                setCreateTicketOpen(false);
              }}
            >
              Create
            </button>
          </div>
        </div>
      </Modal>

      {/* Session Info */}
      <Modal show={sessionOpen} onClose={() => setSessionOpen(false)} title="Session Information" maxWidth="max-w-2xl">
        <div className="space-y-2 text-sm">
          <Row label="Location" value="Delhi, India" />
          <Row label="IP" value="2401:4900:1c5c:f3a6:c48e:a7:1430:7e7e" />
          <Row
            label="Platform"
            value="Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/141.0.0.0 Safari/537.36"
          />
          <Row label="Referrer" value="https://www.anlook.com/" />
          <Row label="Duration" value="—" />
        </div>
      </Modal>

      {/* Copy */}
      <Modal show={copyOpen} onClose={() => setCopyOpen(false)} title="Copy Conversation" maxWidth="max-w-3xl">
        <div className="rounded-xl border bg-gray-50 p-3 text-xs leading-relaxed">
          <pre className="whitespace-pre-wrap">{transcriptText}</pre>
        </div>
        <div className="mt-3 flex justify-end">
          <button
            className="h-9 rounded-xl bg-[#01C37D] px-4 text-sm font-medium text-white"
            onClick={() => {
              copyTranscript();
              setCopyOpen(false);
            }}
          >
            Copy
          </button>
        </div>
      </Modal>

      {/* Ban */}
      <Modal show={banOpen} onClose={() => setBanOpen(false)} title="Ban User" maxWidth="max-w-md">
        <div className="space-y-3">
          <div className="text-sm">Why do you want to ban {activeUser?.id}?</div>
          <textarea rows={5} className="w-full rounded-xl border px-3 py-2 text-sm outline-none" />
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" className="h-4 w-4" checked={banIp} onChange={(e) => setBanIp(e.target.checked)} />
            Ban IP
          </label>
          <div className="flex justify-end">
            <button
              className="h-9 rounded-xl bg-rose-600 px-4 text-sm font-medium text-white hover:bg-rose-700"
              onClick={() => {
                toast.success("User banned");
                setBanOpen(false);
              }}
            >
              Ban
            </button>
          </div>
        </div>
      </Modal>

      {/* AI Feedback */}
      <Modal show={feedbackOpen} onClose={() => setFeedbackOpen(false)} title="AI Assist Feedback" maxWidth="max-w-2xl">
        <div className="space-y-3">
          <Input label="Name" />
          <Input label="Email Address" />
          <Input label="How satisfied are you with AI Assist?" />
          <Textarea label="Could you elaborate on your satisfaction?" rows={3} />
          <Textarea label="What should be the next addition or improvement?" rows={3} />
          <Textarea label="Did you encounter any issues/bugs?" rows={3} />
          <div>
            <div className="mb-1 text-xs text-gray-500">Attachment</div>
            <div className="rounded-xl border border-dashed p-6 text-center text-sm text-gray-500">
              Drop your files here or <span className="text-[#01C37D]">browse</span>
            </div>
          </div>
          <Textarea label="Anything else you want to share?" rows={3} />
          <button
            className="mt-2 h-10 w-full rounded-xl bg-[#01C37D] text-sm font-medium text-white"
            onClick={() => {
              toast.success("Submitted");
              setFeedbackOpen(false);
            }}
          >
            Submit
          </button>
          <div className="text-center text-sm text-gray-500">Report Abuse</div>
        </div>
      </Modal>

      {/* Tag quick create (simple) */}
      <Modal show={tagModalOpen} onClose={() => setTagModalOpen(false)} title="New Tag" maxWidth="max-w-sm">
        <div className="space-y-3">
          <Input label="Tag name" value={newTag} onChange={(v) => setNewTag(v)} />
          <div className="flex justify-end gap-2">
            <button className="h-9 rounded-xl border px-3 text-sm" onClick={() => setTagModalOpen(false)}>
              Cancel
            </button>
            <button
              className="h-9 rounded-xl bg-[#01C37D] px-4 text-sm font-medium text-white"
              onClick={() => {
                if (newTag.trim() && !tags.includes(newTag.trim())) setTags((t) => [...t, newTag.trim()]);
                setNewTag("");
                setTagModalOpen(false);
              }}
            >
              Create
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

/** Generic modal */
function Modal({ show, onClose, title, children, maxWidth = "max-w-xl" }) {
  return (
    <Transition show={show} as={Fragment}>
      <Dialog onClose={onClose} className="relative z-50">
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-150"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" />
        </Transition.Child>

        <div className="fixed inset-0 grid place-items-center p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-150"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-100"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className={cls("w-full rounded-2xl bg-white p-5 shadow-xl", maxWidth)}>
              <div className="mb-3 flex items-center justify-between">
                <Dialog.Title className="text-base font-semibold">{title}</Dialog.Title>
                <button className="h-8 rounded-xl border px-3 text-sm" onClick={onClose}>
                  Close
                </button>
              </div>
              <div className="max-h-[70vh] overflow-auto">{children}</div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}

function Row({ label, value }) {
  return (
    <div className="grid grid-cols-3 items-start gap-2 rounded-xl border p-2">
      <div className="col-span-1 text-xs font-semibold text-gray-500">{label}</div>
      <div className="col-span-2 text-sm">{value}</div>
    </div>
  );
}
function Input({ label, value, onChange }) {
  return (
    <label className="block text-sm">
      <div className="mb-1 text-xs text-gray-500">{label}</div>
      <input
        className="h-10 w-full rounded-xl border px-3 text-sm outline-none"
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
      />
    </label>
  );
}
function Textarea({ label, rows = 3, value, onChange }) {
  return (
    <label className="block text-sm">
      <div className="mb-1 text-xs text-gray-500">{label}</div>
      <textarea
        rows={rows}
        className="w-full rounded-xl border px-3 py-2 text-sm outline-none"
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
      />
    </label>
  );
}
